import React, { useEffect, useState, useRef } from 'react';
import { Col, Form, Row, Popover, Overlay, Button } from "react-bootstrap";
import Select from "react-select";
import { FadeLoader } from "react-spinners";
import { toast } from "react-toastify";
import { EmptyLocalStorage } from "../../services/auth/auth.service";
import { useHistory } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { SentNotification } from "../../services/notification.service";
import { GetUsers } from "../../services/users.service";
import { MdOutlineFilterAlt } from "react-icons/md";
import moment from 'moment';

function Notifications(props) {
    let history = useHistory();
    const [users, setUsers] = useState([]);
    const [loader, setLoader] = useState(false);
    const [showFilter, setShowFilter] = useState(false);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [filterSettings, setFilterSettings] = useState({
        allUsers: false,
        selectedCohort: null
    });

    const target = useRef(null);

    const { register, handleSubmit, control, reset, setValue, formState: { errors } } = useForm({
        mode: "onBlur",
    });

    const cohortOptions = [
        { label: 'Cohort 1 (Last 7 days)', value: 'Cohort 1' },
        { label: 'Cohort 2 (8-30 days)', value: 'Cohort 2' },
        { label: 'Cohort 3 (31-90 days)', value: 'Cohort 3' },
        { label: 'Cohort 4 (91-365 days)', value: 'Cohort 4' },
        { label: 'Cohort 5 (1-2 years)', value: 'Cohort 5' },
        { label: 'Cohort 6 (2-3 years)', value: 'Cohort 6' }
    ];

    const customStyles = {
        control: (provided) => ({
            ...provided,
            minHeight: 50,
        }),
        valueContainer: (provided) => ({
            ...provided,
            maxHeight: '100px',
            overflow: 'auto',
        }),
        multiValue: (provided) => ({
            ...provided,
            margin: '2px',
        }),
        menu: (provided) => ({
            ...provided,
            maxHeight: '200px',
        }),
        menuList: (provided) => ({
            ...provided,
            maxHeight: '200px',
        }),
    };

    const calculateCohort = (selectedCohort) => {
        const now = moment();
        let start, end;

        switch (selectedCohort) {
            case 'Cohort 1':
                start = now.clone().subtract(6, 'days');
                end = now;
                break;
            case 'Cohort 2':
                start = now.clone().subtract(30, 'days');
                end = now.clone().subtract(7, 'days');
                break;
            case 'Cohort 3':
                start = now.clone().subtract(90, 'days');
                end = now.clone().subtract(31, 'days');
                break;
            case 'Cohort 4':
                start = now.clone().subtract(1, 'year');
                end = now.clone().subtract(91, 'days');
                break;
            case 'Cohort 5':
                start = now.clone().subtract(2, 'years');
                end = now.clone().subtract(1, 'year').add(1, 'day');
                break;
            case 'Cohort 6':
                start = now.clone().subtract(3, 'years');
                end = now.clone().subtract(2, 'years').add(1, 'day');
                break;
            default:
                start = null;
                end = null;
                break;
        }

        return {
            startDate: start.toDate(),
            endDate: end.toDate()
        };
    };

    const getUsers = async (cohort = null) => {
        setLoader(true);
        try {
            let result;
            if (cohort) {
                const { startDate, endDate } = calculateCohort(cohort);
                result = await GetUsers(
                    false, '', '', '', 0, '', null,
                    startDate, endDate
                );
            } else {
                result = await GetUsers(false, '', '', '', 0, '', null);
            }
    
            if (result.status && result.data) {
                const userOptions = result.data.map(e => ({
                    label: `${e.fullName} (${e.email})`,
                    value: e._id
                }));
                setUsers(userOptions); // Set the user options in the state
    
                // If selecting all users, update the selection
                if (filterSettings.allUsers) {
                    setValue('selectedUsers', userOptions);
                    setSelectedUsers(userOptions);
                }
    
                return userOptions; // Return the options for use in handleFilterChange
            } else {
                // If no users are returned
                return []; // Return an empty array if no users are found
            }
        } catch (error) {
            console.error('Error fetching users:', error);
            toast.error("Failed to fetch users");
            return []; // Return an empty array on error
        } finally {
            setLoader(false); // Always reset loader here
        }
    };
    
    


    useEffect(() => {
        getUsers();
    }, []);

    useEffect(() => {
        return () => {
            getUsers();
            setFilterSettings({
                allUsers: false,
                selectedCohort: null
            });
        };
    }, []);

    const handleFilterChange = async (type, value) => {
        if (type === 'allUsers') {
            setFilterSettings(prev => ({
                ...prev,
                allUsers: value,
                selectedCohort: null // Reset cohort when selecting all users
            }));
            if (value) {
                // When selecting all users, fetch and auto-select all users
                const allUsers = await getUsers(); // Ensure this fetches the full user list
                setValue('selectedUsers', allUsers); // Update the form field
                setSelectedUsers(allUsers); // Update the selected users state
            } else {
                // When deselecting all users, clear selection
                setValue('selectedUsers', []); // Clear the form field
                setSelectedUsers([]); // Clear the selected users state
            }
        } else if (type === 'cohort') {
            setFilterSettings(prev => ({
                allUsers: false, // Reset allUsers when selecting a cohort
                selectedCohort: value
            }));
            if (value) {
                // When selecting a cohort, fetch and auto-select users
                const cohortUsers = await getUsers(value);
                if (cohortUsers?.length > 0) {
                    setValue('selectedUsers', cohortUsers); // Update the form field
                    setSelectedUsers(cohortUsers); // Update the selected users state
                } else {
                    // If no users in the cohort, clear the selection
                    setValue('selectedUsers', []); // Clear the form field
                    setSelectedUsers([]); // Clear the selected users state
                    toast.info("No users found in this cohort"); // Optional: Notify the user
                }
            } else {
                // When clearing cohort, reset to all users without selection
                const allUsers = await getUsers(); // Fetch all users if no cohort is selected
                setValue('selectedUsers', []); // Clear the form field
                setSelectedUsers([]); // Clear the selected users state
            }
        }
    };
    
    

    const onSubmit = async data => {
        setLoader(true);

        let dt = {
            title: data.title,
            message: data.body,
            type: data.type,
        }

        if (filterSettings.allUsers) {
            dt.allUsers = true;
        } else {
            dt.users = data.selectedUsers.map(user => user.value);
        }

        try {
            const response = await SentNotification(dt);
            if (response.status) {
                toast.success(response.message);
                reset();
                setValue('selectedUsers', []);
                setSelectedUsers([]);
                setFilterSettings({
                    allUsers: false,
                    selectedCohort: null
                });
            } else {
                toast.error(response.message);
            }
        } catch (error) {
            if (error.response?.status === 401) {
                EmptyLocalStorage();
                history.push('/');
            } else {
                toast.error(error.response?.data?.message || "An error occurred");
            }
        } finally {
            setLoader(false); // Always reset loader here
        }
        // setLoader(false);
    };

    return (
        <div className="EditVideo">
            <h3 className="page-heading">Notifications</h3>
            <hr />
            <Row>
                <Col xs={12} sm={12} md={10} lg={12} xl={6}>
                    <Form className="formAddUser" autoComplete="off" role="presentation"
                        onSubmit={handleSubmit(onSubmit)}>
                        <Row>
                            <Col xs={12} sm={12} md={12} lg={12} xl={12}>
                                <Form.Group className="mb-3" controlId="name">
                                    <Form.Label>Title*</Form.Label>
                                    <Form.Control
                                        placeholder="Enter Title"
                                        {...register('title', {
                                            required: "Title is required",
                                            pattern: {
                                                value: /^(?!\s).*$/,
                                                message: "Title should not start with a space"
                                            },
                                            minLength: {
                                                value: 2,
                                                message: "Min length is 2"
                                            },
                                            maxLength: {
                                                value: 100,
                                                message: "Title cannot exceed 100 characters"
                                            }
                                        })}
                                    />
                                    {errors.title && <Form.Text className="text-muted validationText hasError">{errors.title.message}</Form.Text>}
                                </Form.Group>
                            </Col>
                            <Col xs={12} sm={12} md={12} lg={12} xl={12}>
                                <Form.Group className="mb-3" controlId="text">
                                    <Form.Label>Body*</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={6}
                                        placeholder="Enter Body"
                                        {...register('body', {
                                            required: "Body is required",
                                            pattern: {
                                                value: /^(?!\s).*$/,
                                                message: "Body should not start with a space"
                                            },
                                            minLength: {
                                                value: 3,
                                                message: "Min length is 3"
                                            },
                                            maxLength: {
                                                value: 250,
                                                message: "Body cannot exceed 250 characters"
                                            }
                                        })}
                                    />
                                    {errors.body && <Form.Text className="validationText hasError">{errors.body.message}</Form.Text>}
                                </Form.Group>
                            </Col>
                            {/* <Col xs={12} sm={12} md={12} lg={12} xl={12}>
                                <Form.Group className="mb-3" controlId="users">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <Form.Label>Users</Form.Label>
                                        <Button
                                            ref={target}
                                            variant="outline-secondary"
                                            size="sm"
                                            onClick={() => setShowFilter(!showFilter)}
                                            disabled={loader}
                                        >
                                            <MdOutlineFilterAlt size={16} className="me-1" />
                                            Filter Users
                                        </Button>
                                    </div>

                                    <Overlay
                                        target={target.current}
                                        show={showFilter}
                                        placement="bottom-end"
                                        rootClose
                                        onHide={() => setShowFilter(false)}
                                    >
                                        <Popover id="popover-filter" className="shadow-sm">
                                            <Popover.Body>
                                                <div className="mb-2">
                                                    <Form.Check
                                                        type="checkbox"
                                                        id="allUsers"
                                                        label="Select All Users"
                                                        checked={filterSettings.allUsers}
                                                        onChange={(e) => handleFilterChange('allUsers', e.target.checked)}
                                                        disabled={loader}
                                                    />
                                                </div>
                                                <div>
                                                    <Form.Label className="mb-1">Filter by Cohort</Form.Label>
                                                    <Select
                                                        value={cohortOptions.find(option => option.value === filterSettings.selectedCohort)}
                                                        onChange={(option) => handleFilterChange('cohort', option?.value)}
                                                        options={cohortOptions}
                                                        isClearable
                                                        isDisabled={filterSettings.allUsers || loader}
                                                        placeholder="Select cohort..."
                                                    />
                                                </div>
                                            </Popover.Body>
                                        </Popover>
                                    </Overlay>

                                    <Controller
                                        name="selectedUsers"
                                        control={control}
                                        rules={{
                                            validate: value => filterSettings.allUsers || (Array.isArray(value) && value.length > 0) || "Please select at least one user"
                                        }}
                                        render={({ field }) => (
                                            <Select
                                                {...field}
                                                isMulti
                                                options={users}
                                                styles={customStyles}
                                                className="basic-multi-select"
                                                isDisabled={loader}
                                                placeholder={loader ? "Loading users..." : "Choose users"}
                                            />
                                        )}
                                    />
                                    {errors.selectedUsers && <Form.Text className="text-muted validationText hasError">{errors.selectedUsers.message}</Form.Text>}
                                </Form.Group>
                            </Col> */}
                            <Col xs={12} sm={12} md={12} lg={12} xl={12}>
                                <Form.Group className="mb-3" controlId="users">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <Form.Label>Users</Form.Label>
                                        <Button
                                            ref={target}
                                            // variant="outline-secondary"
                                            size="sm"
                                            onClick={() => setShowFilter(!showFilter)}
                                            disabled={loader}
                                            className='btn btn-green-theme'
                                        >
                                            {/* <MdOutlineFilterAlt size={16} className='me-1'/> */}
                                            Filter Users
                                        </Button>
                                    </div>

                                    <Overlay
                                        target={target.current}
                                        show={showFilter}
                                        placement="bottom-end"
                                        rootClose
                                        onHide={() => setShowFilter(false)}
                                    >
                                        <Popover id="popover-filter" className="shadow-sm" style={{ maxWidth: '300px', width: '100%' }}>
                                            <Popover.Body>
                                                <div className="mb-2">
                                                    <Form.Check
                                                        type="checkbox"
                                                        id="allUsers"
                                                        label="Select All Users"
                                                        checked={filterSettings.allUsers}
                                                        onChange={(e) => handleFilterChange('allUsers', e.target.checked)}
                                                        disabled={loader}
                                                    />
                                                </div>
                                                <div>
                                                    <Form.Label className="mb-1">Filter by Cohort</Form.Label>
                                                    <Select
                                                        value={cohortOptions.find(option => option.value === filterSettings.selectedCohort)}
                                                        onChange={(option) => handleFilterChange('cohort', option?.value)}
                                                        options={cohortOptions}
                                                        isClearable
                                                        isDisabled={filterSettings.allUsers || loader}
                                                        placeholder="Select cohort..."
                                                    />
                                                </div>
                                            </Popover.Body>
                                        </Popover>
                                    </Overlay>

                                    <Controller
                                        name="selectedUsers"
                                        control={control}
                                        rules={{
                                            validate: value => filterSettings.allUsers || (Array.isArray(value) && value.length > 0) || "Please select at least one user"
                                        }}
                                        render={({ field }) => (
                                            <Select
                                                {...field}
                                                isMulti
                                                options={users}
                                                styles={customStyles}
                                                className="basic-multi-select"
                                                isDisabled={loader}
                                                placeholder={loader ? "Loading users..." : "Choose users"}
                                                isClearable={false} // Remove clear button
                                                value={selectedUsers} // Controlled value
                                                onChange={(newValue) => {
                                                    field.onChange(newValue);
                                                    setSelectedUsers(newValue);
                                                }}
                                            />
                                        )}
                                    />
                                    {errors.selectedUsers && <Form.Text className="text-muted validationText hasError">{errors.selectedUsers.message}</Form.Text>}
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col xs={12} sm={12} md={8} lg={6} xl={6}>
                                {loader ? (
                                    <div className="spin-loader">
                                        <FadeLoader color={"#41D2D2"} height={10} />
                                    </div>
                                ) : (
                                    <button type="submit" className="btn btn-green-theme w-100 mt-3">
                                        Send
                                    </button>
                                )}
                            </Col>
                        </Row>
                    </Form>
                </Col>
            </Row>
        </div>
    );
}

export default Notifications;